import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bot, CheckCircle2, Inbox, Info, Minus, XCircle } from "lucide-react";
import { userName, useUsers } from "../lib/hooks";

const TONE_ICON = { success: CheckCircle2, warning: AlertTriangle, danger: XCircle, info: Info, ai: Bot, muted: Minus };

export function Chip({ map, value, testid }) {
  const conf = map?.[value] || { label: value || "—", tone: "muted" };
  const Icon = TONE_ICON[conf.tone] || Minus;
  return <span data-testid={testid} className={`ts-chip ts-chip-${conf.tone}`}><Icon className="h-3 w-3" aria-hidden="true" />{conf.label}</span>;
}

export function ToneChip({ label, tone = "muted", testid }) {
  const Icon = TONE_ICON[tone] || Minus;
  return <span data-testid={testid} className={`ts-chip ts-chip-${tone}`}><Icon className="h-3 w-3" aria-hidden="true" />{label}</span>;
}

export function KpiCard({ title, value, subtitle, icon: Icon, tone = "info", to, testid }) {
  const tones = {
    danger: "text-[#B91C1C]", warning: "text-[#B45309]", success: "text-[#15803D]",
    info: "text-[#001C64]", ai: "text-[#6D28D9]", muted: "text-[#5B6577]",
  };
  const inner = (
    <div className="ts-kpi group" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="ts-caption">{title}</p>
          <p className={`mt-1 font-display text-3xl font-bold tracking-tight ${tones[tone]}`}>{value}</p>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`rounded-sm border border-slate-200 bg-slate-50 p-2 ${tones[tone]}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-700 opacity-0 transition-opacity group-hover:opacity-100">
        Ver detalle <ArrowRight className="h-3 w-3" />
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export function FactStrip({ facts, testid }) {
  const items = (facts || []).filter((f) => f.value !== null && f.value !== undefined && f.value !== "" && f.value !== "—");
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 xl:grid-cols-6" data-testid={testid || "fact-strip"}>
      {items.map((f) => (
        <div key={f.label}>
          <p className="ts-caption">{f.label}</p>
          <p className={`mt-0.5 text-sm font-semibold ${f.mono ? "font-mono tracking-tight" : ""} ${f.tone === "danger" ? "text-[#B91C1C]" : f.tone === "success" ? "text-[#15803D]" : "text-slate-900"}`} data-testid={`fact-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{f.value}</p>
        </div>
      ))}
    </div>
  );
}

export function BrandMark({ compact = false }) {
  return (
    <div className="ts-brand-mark" data-testid="brand-mark">
      <div className="ts-brand-sig-inverse">TS</div>
      {!compact && (
        <div className="min-w-0 leading-tight">
          <p className="font-display text-sm font-bold tracking-wide text-white">TECH STONE</p>
          <p className="text-[10px] uppercase tracking-widest text-[#8A9CC9]">Workspace · Demo</p>
        </div>
      )}
    </div>
  );
}

export function EmptyState({ title, description, action, testid }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50/50 px-6 py-12 text-center" data-testid={testid || "empty-state"}>
      <Inbox className="mb-3 h-8 w-8 text-slate-300" />
      <p className="font-medium text-slate-700">{title}</p>
      {description && <p className="mt-1 max-w-md text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function RestrictedState({ module, detail, testid }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-slate-200 bg-slate-50 px-6 py-12 text-center" data-testid={testid || "restricted-state"}>
      <AlertTriangle className="mb-3 h-8 w-8 text-slate-300" />
      <p className="font-medium text-slate-700">Acceso restringido</p>
      <p className="mt-1 max-w-md text-sm text-slate-500">{detail || `Tu perfil externo no tiene acceso a ${module}. Si necesitas un documento o dato concreto, solicítalo al equipo interno.`}</p>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid={testid || "page-title"}>{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function UserLabel({ id, className = "" }) {
  const { map } = useUsers();
  return <span className={className}>{userName(map, id)}</span>;
}

export function ReasonsList({ reasons, testid }) {
  const { map } = useUsers();
  if (!reasons?.length) return null;
  return (
    <div className="space-y-2" data-testid={testid || "blocked-reasons"}>
      {reasons.map((r, i) => (
        <div key={i} className="flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid={`blocked-reason-${i}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-900">{r.message}</p>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-amber-700">
              {r.resolver_id && <span>Responsable de resolverlo: <strong>{userName(map, r.resolver_id)}</strong></span>}
              {r.link && <Link to={r.link} className="font-medium text-blue-700 underline underline-offset-2">Ir al requisito →</Link>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
